// These types are used by both backend and frontend

import { AnalysisKey, RoleId } from './commonConstants';
import { ObjectId } from 'mongodb';

export type EngineId = 'Creative' | 'Games' | 'Innovative' | 'Create';

export type BaseUserColour = 'orange' | 'fuchsia' | 'red' | 'green';
export type AdditionalUserColour = 'dark' | 'violet' | 'yellow' | 'cyan';
export type ExtraUserColour =
	| 'blue'
	| 'pink'
	| 'lime'
	| 'teal'
	| 'maroon'
	| 'turquoise'
	| 'purple'
	| 'navy'
	| 'salmon'
	| 'forestGreen'
	| 'olive'
	| 'chocolate';

export type AvailableColour =
	| BaseUserColour
	| AdditionalUserColour
	| ExtraUserColour
	| 'grey4'
	| 'IDEATE';

// Created by empathise tool, or link tool
export type EmpathiseColour =
	| 'FOREST'
	| 'RED'
	| 'FUSCHIA'
	| 'BLUE'
	| 'TAN'
	| 'SALMON'
	| 'GREENBLUE'
	| 'GREEN'
	| 'LINK'
	| 'IDEATE';

export const Perspectives: Record<EmpathiseColour, string> = {
	FOREST: 'Semantic association',
	TAN: 'Contextual association',
	RED: 'Negative association',
	FUSCHIA: 'Emotional association',
	BLUE: 'Co-occurence',
	SALMON: 'Semantic association',
	GREEN: 'Positive association',
	GREENBLUE: 'Creative association', // Not used
	LINK: 'Linking association', // Not used
	IDEATE: 'Creative association', // Not used
};

export type Point = {
	x: number;
	y: number;
};

export type VelocityObject = {
	vx?: number;
	vy?: number;
};

export type FixedPoint = {
	fx?: number;
	fy?: number;
};

export type LineSegment = {
	from: Point;
	to: Point;
};

export type MergedNode = {
	label?: string | null;
	nodeColour?: EmpathiseColour;
	mergedFrom?: Array<MergedNode>;
	linkedNodeIds?: Array<string>;
};

export type NCNode = Point &
	VelocityObject &
	FixedPoint & {
		uid: string;
		created: number;
		/** Per-user `user:<id>` flags. Colour keys are retained for legacy boards. */
		likes?: { [key: string]: boolean };
		style?: 'rect' | undefined; // Normally undefined, but can be set to 'rect' for nodes that are not circles
		colour: AvailableColour; // Usually determined by user who created the node
		nodeColour?: EmpathiseColour;
		label?: string | null;
		href?: string;
		src?: string;
		isVideo?: boolean;
		hidden?: boolean;
		dotted?: boolean;
		merged?: boolean;
		moving?: boolean;
		mergedFrom?: Array<MergedNode>;
		index?: number;
		parent?: {
			uid: string;
			label: string;
		};
		topics?: string[];
		pdfInsightId?: string;
		pdfAnalysisId?: string;
		pdfFileName?: string;
		pdfProvenance?: 'legacy_derived' | 'source_extracted' | 'source_inferred';
		synthesiseRecipe?: SynthesiseRecipe;
		/** Structured snapshot of an Idea Box output materialised on the board. */
		positioningOutput?: PositioningOutputSnapshot;
		/** Ephemeral rich evidence used in Ideate's Grounding Context explorer. */
		groundingEvidence?: GroundingEvidence;
	};

export type SynthesiseRecipeInput = {
	id: string;
	role: string;
	label: string;
	value?: string;
	src?: string;
	nodeUid?: string;
	source: 'board' | 'idea-box' | 'notepad' | 'manual';
};

export type SynthesiseRecipe = {
	task: string;
	inputs: SynthesiseRecipeInput[];
	instructions?: string;
	settings?: {
		tone?: string;
		length?: string;
		format?: string;
		platform?: 'LinkedIn' | 'Reddit' | 'X' | 'Instagram';
	};
	inputSnapshot: string;
	generatedAt: number;
};

export type NCGroup = {
	uid: string;
	nodes: Array<NCNode>;
	colour: AvailableColour;
	overrideColour?: string;
	active?: boolean;
	lock?: boolean;
};

export type NCGroupMinimal = {
	uid: string;
	nodes: Array<{ uid: string }>;
	colour: AvailableColour;
	overrideColour?: string;
	active?: boolean;
	lock?: boolean;
};

export type NCLink = {
	source: NCNode;
	target: NCNode;
	colour: AvailableColour;
};

export type NCLinkMinimal = {
	source: { uid: string };
	target: { uid: string };
	colour: AvailableColour;
};

export type NCCluster = {
	group: string;
	values: string[];
	likedNodes?: Array<string>;
	liked?: boolean;
	/** Optional dashed-ring radius override (e.g. Grounding context board). */
	radius?: number;
	/** Optional persisted layout for analyses whose cluster sizes vary substantially. */
	layout?: {
		x: number;
		y: number;
		orbitRadius: number;
	};
};

export type GenerateTask =
	| 'haiku'
	| 'ai-haiku'
	| 'proverb'
	| 'brainstorm'
	| 'common'
	| 'persona'
	| 'character'
	| 'story'
	| 'brand-colours'
	| 'brand-tone'
	| 'audience'
	| 'motivations'
	| 'image-inspire'
	| 'image-synthesise'
	| 'video-inspire';

export type PositioningAnalysisSource = {
	analysis: AnalysisKey;
	analysisName: string;
	cluster: string;
	nodes: string[];
};

/**
 * Structured board-first grounding captured at Ideate generation time.
 * Foundations shows these sections; board/liked selections can be trimmed before regen.
 * Live web/social selections live in `groundingEvidence` (kept separate for backward compat).
 */
export type IdeateGroundingSnapshot = {
	/** Project framing lines used in the prompt (title, goal, vision, …). */
	framing?: GroundingEvidence[];
	/** Inspire/Ideate board concepts scored into the prompt. */
	boardConcepts?: GroundingEvidence[];
	/** Idea Box / liked research used in the prompt. */
	likedResearch?: GroundingEvidence[];
	/** Flowy memory / conversation snippets when available. */
	flowy?: GroundingEvidence[];
	/** Social poster profiles required for persona/motivations (catalog at generation time). */
	socialProfiles?: GroundingEvidence[];
	/**
	 * Selected framing lines for regeneration. When omitted, all `framing` are used.
	 * Foundations may narrow this the same way as board / Idea Box / social selections.
	 */
	selectedFraming?: GroundingEvidence[];
	/**
	 * Selected board concepts for regeneration. When omitted, all `boardConcepts` are used.
	 * Foundations may narrow this list the same way as live evidence.
	 */
	selectedBoardConcepts?: GroundingEvidence[];
	/** Selected liked research for regeneration. When omitted, all `likedResearch` are used. */
	selectedLikedResearch?: GroundingEvidence[];
	/** Selected social poster profiles for regeneration. When omitted, all `socialProfiles` are used. */
	selectedSocialProfiles?: GroundingEvidence[];
	/** Qualitative, evidence-linked stance/sentiment/emotion landscape for Foundations. */
	socialSegments?: SocialLandscapeSegment[];
	/** Segment IDs explicitly selected for the next regeneration. */
	selectedSocialSegmentIds?: string[];
};

export type SocialLandscapeSegment = {
	id: string;
	label: string;
	dimension: 'stance' | 'sentiment' | 'emotion';
	value: string;
	evidenceIds: string[];
	observationCount: number;
	platformCounts: Record<string, number>;
};

/**
 * Evidence assignment for one Ideate perspective. This is deliberately a
 * retrieved-sample record, not a claim about a population segment.
 */
export type SocialPerspectiveBlueprint = {
	primarySegmentId: string;
	primaryStance: 'supportive' | 'opposed' | 'ambivalent' | 'unclear';
	sentiment?: 'positive' | 'neutral' | 'negative' | 'mixed';
	emotions: string[];
	evidenceIds: string[];
	sharedEvidenceIds: string[];
	counterSignalEvidenceIds: string[];
	bundleId?: string;
	bundleVersion?: string;
};

export type PositioningGenerationContext = {
	task: GenerateTask;
	perspective: string;
	perspectiveDescription?: string;
	projectVision?: string;
	sources: PositioningAnalysisSource[];
	analysisSnapshots?: Array<{
		analysis: AnalysisKey;
		clusters: NCCluster[];
	}>;
	/** Snapshot of live web/social evidence used for this output. Users may remove items before regeneration. */
	groundingEvidence?: GroundingEvidence[];
	/** Full board-first grounding used for this output (persisted for View Foundations). */
	groundingSnapshot?: IdeateGroundingSnapshot;
	/** Distinct social lane assigned before generation, retained with the output lineage. */
	socialPerspectiveBlueprint?: SocialPerspectiveBlueprint;
};

export type PositioningSourceContext = {
	projectFraming: string;
	pdfOverview?: string;
	pdfTopics: Array<{
		topic: string;
		extractedText: string;
	}>;
	analysisTerms: string[];
};

export const SYNTHESISE_TASKS = [
	'audience',
	'brand-strategy',
	'product-idea',
	'product-brief',
	'technical-spec',
	'game',
	'summary',
	'extract',
	'unite',
	'taglines',
	'brainstorm',
	'synopsis',
	'linkedinpost',
] as const;
export const SYNTHESISE_SUBTASKS = [
	'pestel-p',
	'pestel-e',
	'pestel-s',
	'pestel-t',
	'pestel-env',
	'pestel-l',
	'swot-s',
	'swot-w',
	'swot-o',
	'swot-t',
	'scamper-s',
	'scamper-c',
	'scamper-a',
	'scamper-m',
	'scamper-p',
	'scamper-e',
	'scamper-r',
] as const;
export type SynthesiseSubTask = (typeof SYNTHESISE_SUBTASKS)[number];
export type SynthesiseTask = (typeof SYNTHESISE_TASKS)[number];

export type SearchTask = 'search-image' | 'search-page' | 'article';

export type LikedItem = {
	_id?: string;
	clientId?: string;
	type:
		| GenerateTask
		| SynthesiseTask
		| SynthesiseSubTask
		| SearchTask
		| 'node'
		| 'cluster'
		| 'linkedinpost';
	text?: string;
	title?: string;
	url?: string;
	topics?: string[];
	positioningContext?: PositioningGenerationContext;
	/** Stable identity shared by every version of this saved result. */
	sourceOutputId?: string;
	/** Monotonically increasing version of the saved source content. */
	outputVersion?: number;
	/** Structured, render-independent snapshots derived from this saved result. */
	structuredOutputs?: PositioningOutputSnapshot[];
	/** User ids that have liked this saved positioning-map output. */
	likedByUserIds?: string[];
	/** Reaction state projected for the user requesting the output. */
	likedByCurrentUser?: boolean;
	likeCount?: number;
	likedByUsers?: Array<{ id: string; name: string; colour: string }>;
	isOwner?: boolean;
	/** Contributor metadata returned for shared Team-board outputs. */
	addedBy?: {
		id: string;
		name: string;
		colour: string;
	};
	/** When false, the like stays on the personal Ideate/Synthesise board until shared. */
	sharedToTeam?: boolean;
	// uid of the originating Inspire node, used to link this LikedItem to a
	// corresponding node on the user's personal Synthesise (Moodboard) board.
	// Only set for items hearted from an Inspire-board node (node / image-inspire / video-inspire).
	nodeUid?: string;
};

export type PositioningLayoutPoint = { x: number; y: number };

/** Saved Ideate positioning-map coordinates, keyed by stable map node id. */
export type PositioningLayout = Record<string, PositioningLayoutPoint>;

export type NCGraphData = {
	template?: string;
	positioningInsight?: string;
	positioningLayout?: PositioningLayout;
	nodes: NCNode[];
	links: NCLinkMinimal[];
	groups?: NCGroupMinimal[];
	clusters?: NCCluster[];
	analyses?: AnalysisKey[];
	activeAnalysis?: AnalysisKey;
	uploadedFiles?: UserFile[];
};

export type BoardUser = {
	id: string;
	username: string;
	email: string;
	isOnline: boolean;
	lastSeen: number | undefined; // timestamp
	activeNode?: string | undefined;
	owner?: boolean;
	settings: {
		ai_enabled: boolean;
		shown_inspire?: boolean;
		shown_ideate?: boolean;
		ideateWelcomeVersion?: string;
		shown_synthesise?: boolean;
		presentationId?: string;
		used?: {
			spark?: boolean;
			empathise?: boolean;
			generate?: boolean;
			search?: boolean;
			link?: boolean;
			explore?: boolean;
		};
	};
	colour: string; // name of colour
	selectedFile?: UserFile;
	/** Explicit per-user PDF context. Missing means every PDF on the current board. */
	pdfContext?: PdfContextRef[];
};

export type TopicAnalysis = {
	key: string;
	topics: string[];
	/** Original wordlookup token before phrase enrichment. */
	rawKey?: string;
	/** Empathise lens for enriched phrases. */
	perspective?: EmpathisePerspective;
	/** Stable local PDF graph identity when the suggestion is evidence-backed. */
	pdfInsightId?: string;
	pdfAnalysisId?: string;
	pdfFileName?: string;
	pdfProvenance?: 'legacy_derived' | 'source_extracted' | 'source_inferred';
	pdfEvidence?: Array<{ chunkId: string; page?: number; text: string }>;
	pdfScore?: number;
};

export type EmpathisePerspective = 'positive' | 'negative' | 'emotional' | 'co_occurrence';

export type UserSelection = {
	tool: 'spark' | 'empathise';
	words: string[];
	timestamp: number;
	topicMapping?: TopicAnalysis[];
};

export type PerUserData = {
	ideate?: { data: NCGraphData };
	ideateTeam?: { data: NCGraphData };
	moodboard?: { data: NCGraphData };
	inspire?: { data: NCGraphData }; // Currently not used
	userSelections?: Array<UserSelection>;
};

export type PdfFileTopic = { topic: string; extracted_text: string };
export type PdfContextRef = { boardId: string; fileId: string };
export type PdfUploadStatus = {
	state: 'processing' | 'ready' | 'failed';
	fileName: string;
	error?: string;
};
export type UserFile = {
	fileId: string;
	vectorStoreId?: string;
	fileName: string;
	fileSummary: string;
	uploadedBy: ObjectId;
	timestamp: number;
	topics?: Array<PdfFileTopic>;
};

export type BoardUserField =
	| 'userOne'
	| 'userTwo'
	| 'userThree'
	| 'userFour'
	| 'userFive'
	| 'userSix'
	| 'userSeven'
	| 'userEight';

export type GraphMode = 'inspire' | 'ideate' | 'moodboard';

export type CapabilityKey =
	| 'create-board'
	| 'board-limit'
	| 'create-project'
	| 'time-limited'
	| 'collaborate-one'
	| 'collaborate-two'
	| 'collaborate-group'
	| 'collaborate-max'
	| 'export'
	| 'slides'
	| 'advanced-ai'
	| 'image-quota';

export type Capability = {
	key: CapabilityKey;
	name: string;
	implemented: boolean;
	type: 'boolean' | 'number';
	max?: number;
};

export interface IRole {
	key: string;
	name: string;
	capabilities: Array<{ capability: CapabilityKey; on: boolean; value?: number }>;
}

export type QueryableRole = IRole & {
	hasCapability: (key: CapabilityKey) => boolean;
	getCapabilityValue: (key: CapabilityKey) => number | undefined;
};

export type SubscriptionFrequency = 'DAY' | 'MONTH' | 'YEAR';

export type ISubscriptionPlan = {
	name: string;
	role: RoleId;
	frequency: SubscriptionFrequency;
	currency: 'GBP';
	offerPrice?: number;
	hasQuantity: boolean;
	hasSeatDiscounts?: boolean;
	hasFreeTrial?: boolean;
	hasMonthTrial?: boolean;
	paypalPlanId: string;
	createdDate: Date;
	isDisabled?: boolean;
};

export type ICustomPlan = {
	code: string;
	seats: number;
	price: number;
	role: string;
	frequency: SubscriptionFrequency;
	paypalPlanId: string;
	createdDate: Date;
	oneOffPeriod?: {
		months: number;
		monthlyPrice: number; // per member
	};
};

export type ClusterAnalysis = {
	name: string;
	category?: string;
	generate?: Array<GenerateTask>;
};

export type SelectableItem<T> = {
	value: T;
	selected: boolean;
	autogenerated?: boolean;
	cached?: boolean;
};

export type TargetAudience = {
	name: string;
	profession: string;
	age: string;
	needs: string;
	motivations: string;
};

export type TextResult = {
	text: string;
};

export type UrlResult = {
	url: string;
};

export type LinkResult = {
	url: string;
	text: string;
};

export interface PresentationInfo {
	_boardId: ObjectId;
	createdDate: Date;
	updatedDate: Date;
	_createdBy: ObjectId;
	title: string;
	date: string;
	vision?: string;
	change?: {
		start: string;
		end: string;
	};
	logistics?: {
		show: boolean;
		deliverables?: string;
		team?: string;
		currency?: string;
		budget?: string;
		deliveryDate?: string;
		firstReviewDate?: string;
		secondReviewDate?: string;
	};
	summary?: string;
	fullName?: string;
	personas: Array<SelectableItem<UserPersona>>;
	motivations: Array<SelectableItem<Motivations>>;
	brandStrategies: Array<SelectableItem<TextResult>>;
	productIdeas: Array<SelectableItem<TextResult>>;
	productBriefs: Array<SelectableItem<TextResult>>;
	targetAudiences: Array<SelectableItem<TargetAudience>>;
	images: Array<SelectableItem<UrlResult>>;
	photos: Array<SelectableItem<UrlResult>>;
	links: Array<SelectableItem<LinkResult>>;
	taglines: Array<SelectableItem<TextResult>>;
	haikus: Array<SelectableItem<TextResult>>;
	brainstorming: Array<SelectableItem<TextResult>>;
	commonIdeas: Array<SelectableItem<TextResult>>;
	conceptualTerritories: Array<
		SelectableItem<{
			phrase: string;
			summary?: string;
			results: Array<SelectableItem<LikedItem>>;
		}>
	>;
}

export const PERSONA_AGE_GROUPS = [
	'Under 18',
	'18–24',
	'25–34',
	'35–44',
	'45–54',
	'55–64',
	'65+',
] as const;
export type PersonaAgeGroup = (typeof PERSONA_AGE_GROUPS)[number];

export const PERSONA_GENDERS = ['female', 'male', 'non-binary'] as const;
export type PersonaGender = (typeof PERSONA_GENDERS)[number];

export type UserPersona = {
	theme: string;
	name: string;
	gender?: PersonaGender;
	ageGroup?: PersonaAgeGroup;
	biography: string;
	goals: string;
	needs: string;
	painPoints: string;
	motivations: string;
	influences: string;
	scores: {
		tech: number;
		ambition: number;
		happiness: number;
	};
	imageUrl?: string;
};

export type BrandPalette = {
	name: string;
	rationale: string;
	colours: Array<{
		name: string;
		hex: string;
		role: string;
	}>;
};

export type Motivations = {
	theme: string;
	intro: string;
	outro: string;
	stakeholders: Array<{
		name: string;
		description: string;
		imageUrl?: string;
	}>;
};

export type PositioningLane = 'audience' | 'motivation' | 'solution' | 'brand' | 'story';

export type PositioningOutputType =
	| 'persona'
	| 'character'
	| 'stakeholder-motivation'
	| 'audience'
	| 'solution'
	| 'brand-colours'
	| 'brand'
	| 'story'
	| 'image'
	| 'video'
	| 'reference'
	| 'concept'
	| 'cluster'
	| 'text';

export type PositioningOutputPayload =
	| { kind: 'persona'; persona: UserPersona }
	| { kind: 'motivation'; motivations: Motivations; stakeholderIndex: number }
	| { kind: 'palette'; palette: BrandPalette }
	| { kind: 'text'; text: string }
	| { kind: 'record'; data: Record<string, unknown>; url?: string }
	| { kind: 'media'; url: string; isVideo: boolean }
	| { kind: 'reference'; text: string; url?: string }
	| { kind: 'cluster'; cluster: NCCluster };

/**
 * Canonical saved-output representation. It contains data only: the results
 * panel, Idea Box and boards render their own density from the same snapshot.
 */
export type PositioningOutputSnapshot = {
	schemaVersion: 1;
	sourceOutputId: string;
	sourceLikeId?: string;
	version: number;
	type: PositioningOutputType;
	sourceType: LikedItem['type'];
	lane?: PositioningLane;
	eyebrow: string;
	title: string;
	subtitle?: string;
	summary: string;
	imageUrl?: string;
	positioningContext?: PositioningGenerationContext;
	payload: PositioningOutputPayload;
	details: Array<{ title: string; value: string }>;
};

export type SearchSource = string;

export type SearchMode = 'mixed' | 'text_only' | 'image_only' | 'visual_similarity';

export type SearchEngagement = {
	likes?: number;
	comments?: number;
	shares?: number;
	reposts?: number;
	retweets?: number;
	replies?: number;
	quotes?: number;
	bookmarks?: number;
	views?: number;
	downloads?: number;
	upvotes?: number;
	upvoteRatio?: number;
	crossposts?: number;
	reactions?: Record<string, number>;
};

export type SearchNativeSentiment = {
	/** Provider-native label; normalized sentiment is introduced separately in Slice 3B. */
	polarity?: string;
	emotions?: Record<string, number>;
	dominantEmotion?: string;
	emotionalIntensity?: number;
};

export type SearchLocation = {
	name?: string;
	city?: string;
	latitude?: number;
	longitude?: number;
};

export type SearchAuthorProfile = {
	id?: string;
	handle?: string;
	displayName?: string;
	followers?: number;
	verified?: boolean;
};

export type SearchResultProvenance = {
	provider: string;
	endpoint: string;
	query: string;
	retrievedAt: string;
	providerResultId?: string;
	providerRequestId?: string;
	requestedOptions?: Record<string, unknown>;
	appliedOptions?: Record<string, unknown>;
	unsupportedOptions?: string[];
};

export type SocialSearchMetadata = {
	subreddit?: string;
	language?: string;
	hashtags?: string[];
	mentions?: string[];
	keywords?: string[];
	mediaType?: string;
	isVideo?: boolean;
	isReply?: boolean;
	isQuote?: boolean;
	isPaidPartnership?: boolean;
};

export type GroundingEvidence = {
	socialBundleId?: string;
	/** Classifier/bundle schema version used to create this evidence. */
	socialBundleVersion?: string;
	socialQueryBriefId?: string;
	socialEvidenceId?: string;
	source: string;
	title: string;
	detail: string;
	/** Full source text for expanded evidence views; `detail` remains prompt-sized. */
	content?: string;
	url?: string;
	/** Social-post display metadata retained for grounded, inspectable foundations. */
	image?: string;
	isVideo?: boolean;
	platform?: string;
	author?: string;
	publishedAt?: string;
	engagement?: SearchEngagement;
	sentiment?: 'positive' | 'neutral' | 'negative' | 'mixed';
	stance?: 'supportive' | 'opposed' | 'ambivalent' | 'unclear';
	emotions?: string[];
	confidence?: number;
	originBoard?: 'inspire' | 'ideate' | 'idea-box';
};

export type SearchEntry = {
	type: 'article' | 'image';
	title?: string;
	url: string;
	snippet?: string;
	/** Full source text when the provider returns it separately from the result preview. */
	content?: string;
	displayLink?: string;
	/** Optional preview/thumbnail (e.g. GDELT socialimage); kept separate from `url` so article-type entries can still render an image badge. */
	image?: string;
	/** Provider-supplied lightweight preview for image-search results. */
	thumbnailUrl?: string;
	/** Page that contains an image-search result, used for attribution/source navigation. */
	contextUrl?: string;
	/** Origin of this result; used by the search panel to render a small source badge. */
	source?: SearchSource;
	/** Social platform when `source` is `social` (e.g. x, instagram). */
	platform?: string;
	/** Poster/author handle or display name when known (social listening). */
	author?: string;
	/** Publication timestamp supplied by the upstream provider. */
	publishedAt?: string;
	/** Provider-native social signals retained for ranking and grounded generation. */
	engagement?: SearchEngagement;
	nativeSentiment?: SearchNativeSentiment;
	location?: SearchLocation;
	authorProfile?: SearchAuthorProfile;
	socialMetadata?: SocialSearchMetadata;
	/** Trace back to the exact provider request and upstream result. */
	provenance?: SearchResultProvenance;
	/** Optional research lens metadata used when Flowy runs multi-angle research. */
	lensId?: string;
	lensLabel?: string;
	/** The user's focal search topic, before engine-lens query expansion. */
	searchTopic?: string;
};
